import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { thumbnailOverflowWrapper } from '@pkg/components/Thumbnail/tokens';
import {
    floorGameColor as color,
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledLayout,
    styledMedia,
    styledReset,
    styledSkeleton,
    styledTypography,
} from '@pkg/primitives';

import { tokens } from '../../DataTable.tokens';

interface ThumbnailProps {
    $border?: boolean;
    $circular?: boolean;
}

interface TextProps {
    $truncate?: boolean;
}

const textBase = css`
    ${styledTypography.allianceBase};
    ${styledTypography.textBoxTrim};
    font-weight: 700;
    letter-spacing: -0.02em;
`;

export const StyledCollection = styled.div`
    ${styledLayout.fullWidthFix};
    position: relative;
    display: flex;
    align-items: center;
    gap: ${tokens.collection.gap}px;
    min-width: ${tokens.collection.minWidth}px;
    width: 100%;
`;

export const ThumbnailWrapper = styled.div<ThumbnailProps>`
    ${thumbnailOverflowWrapper()};
    flex: 0 0 auto;
    width: ${tokens.collection.thumbnail.size}px;
    height: ${tokens.collection.thumbnail.size}px;
    border-radius: 50%;

    ${({ $border }) =>
        $border &&
        css`
            border: 2px solid ${color.coal};
        `};

    ${({ $circular }) =>
        $circular &&
        css`
            // Something
        `};
`;

export const Details = styled.div`
    display: grid;
    justify-items: start;
    gap: 4px;
    flex: 1 1 auto;
`;

export const TitleValue = styled.p<TextProps>`
    ${textBase};
    font-size: ${tokens.collection.title.fontSize}px;

    ${({ $truncate }) =>
        $truncate &&
        css`
            ${styledTypography.truncate};
            ${styledTypography.truncateHeightFix(true)};
            ${styledLayout.fullWidthFix};
        `};
`;

///
/// Subtitle

const subtitleWrapper = css`
    ${styledLayout.fullWidthFix};
    display: flex;
    align-items: center;
    gap: 4px;
    color: rgb(255, 255, 255, 0.6);
`;

export const SubtitleContent = styled.div`
    ${subtitleWrapper};
`;

export const SubtitleLink = styled(Link)`
    ${styledReset.link};
    ${subtitleWrapper};
    transition: color ${duration.normal}ms ${easing.cubic};

    &:hover {
        color: rgb(255, 255, 255, 1);
    }

    &:active {
        color: rgb(255, 255, 255, 0.8);
    }
`;

export const SubtitleValue = styled.span<TextProps>`
    ${textBase};
    display: block;
    flex: 0 1 auto;
    font-size: ${tokens.collection.subtitle.fontSize}px;
    font-variant-numeric: tabular-nums;
    color: currentColor;

    ${({ $truncate }) =>
        $truncate &&
        css`
            ${styledTypography.truncate};
            ${styledTypography.truncateHeightFix(true)};
        `};
`;

export const CurrencyIconWrapper = styled.span`
    ${styledMedia.iconWrapper};
    display: block;
    flex: 0 0 auto;
    width: ${tokens.collection.subtitle.iconSize}px;
    height: ${tokens.collection.subtitle.iconSize}px;
`;

///
/// Skeleton

export const SkeletonThumbnail = styled.div`
    ${styledSkeleton.thumbnailStyles};
`;

export const SkeletonTitle = styled.div`
    ${styledSkeleton.baseStyles};
    flex: 1 1 auto;
    width: 100%;
    height: ${tokens.collection.title.fontSize - tokens.skeleton.heightOffset}px;
    border-radius: 999px;
`;

export const SkeletonSubtitle = styled.div`
    ${styledSkeleton.baseStyles};
    flex: 1 1 auto;
    width: 75%;
    height: ${tokens.collection.subtitle.fontSize - tokens.skeleton.heightOffset}px;
    border-radius: 999px;
`;
